import { useMemo, useState } from 'react'
import { Users, Info } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card } from '@/components/ui'
import { useChartAxisTheme } from '@/hooks/useThemeColors'
import { formatNumber, formatPercentage } from '@/lib/utils'
import { DailyTotals, StudioSubscriptionType } from '@/types'

export interface ClientBaseCardProps {
  dailyData: DailyTotals[]
  clientsTitle: string
  clientsCount?: number
  clientsGrowth?: number
  clientsRetention?: number
  subscribersCount?: number
  subscribersGrowth?: number
  subscribersRetention?: number
  // Посещения: сколько человек приходили в студию >=3 раза за 21 день до даты.
  // Тестовые варианты (3/28д и «база» 1/21д, 1/28д) из миграции 20260625140000
  // на экран не выводятся — колонки в БД остались, выбор определения сделан.
  visitorsCount?: number
  visitorsGrowth?: number
  subscriptionType?: StudioSubscriptionType
}

type ClientBaseMode =
  | 'clients'
  | 'subscribers'
  | 'visitors'

type ClientBaseMetricKey =
  | 'total_clients_count'
  | 'active_subscribers_count'
  | 'active_visitors_count'

interface ModeConfig {
  title: string
  /** Ширина скользящего окна в днях: точка графика — итог этого окна, а не дня. */
  windowDays?: number
  /** Подпись в слоте, где у клиентов стоит «Удержание». */
  note?: string
  /** Та же подпись для узкого экрана: полная теснит само значение. */
  noteShort?: string
  /** Что считает метрика — словами, для тултипа графика. */
  tooltipValueLabel?: string
  dataKey: ClientBaseMode
  metricKey: ClientBaseMetricKey
  count?: number
  growth?: number
  retention?: number
  valueColor: string
  retentionPositiveColor: string
  stroke: string
  fill: string
  axisColor: string
}

function isVisitorMode(mode: ClientBaseMode): boolean {
  return mode === 'visitors'
}

/** Начало скользящего окна: точка за 31.08 при окне 21 день покрывает 11.08–31.08. */
function shiftIsoDate(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  const date = new Date(Date.UTC(year, month - 1, day))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function formatChartDate(date: string) {
  const [, month, day] = date.split('-')
  return `${day}.${month}`
}

function formatTooltipDate(date: string) {
  const [year, month, day] = date.split('-')
  if (!year || !month || !day) return date
  return `${day}.${month}.${year}`
}

export function ClientBaseCard({
  dailyData,
  clientsTitle,
  clientsCount,
  clientsGrowth,
  clientsRetention,
  subscribersCount,
  subscribersGrowth,
  subscribersRetention,
  visitorsCount,
  visitorsGrowth,
  subscriptionType = 'REGULAR'
}: ClientBaseCardProps) {
  const showSubscribers = subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED'
  const [mode, setMode] = useState<ClientBaseMode>('clients')
  // Подписки доступны не всегда — для не-подписочных студий откатываемся на клиентов.
  // Режим «Посещения» был отключён хотфиксом 26.06.2026 (колонки active_visitors_*
  // тогда только появились и по сети ещё не были заполнены). На 02.09.2026 ночной
  // пересчёт закрывает 185–186 студий из 186 открытых, поэтому режим возвращён —
  // но ОДИН, канонический (>=3 визита за 21 день). Три тестовых варианта из
  // миграции 20260625140000 на экран не выводятся: определение выбрано.
  const currentMode: ClientBaseMode = !showSubscribers && mode === 'subscribers' ? 'clients' : mode

  const chartData = useMemo(() => {
    return dailyData
      .filter((day) =>
        day.total_clients_count != null
        || day.active_subscribers_count != null
        || day.active_visitors_count != null
      )
      .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
      .map((day) => ({
        date: formatChartDate(day.metric_date),
        fullDate: day.metric_date,
        clients: day.total_clients_count ?? null,
        subscribers: day.active_subscribers_count ?? null,
        visitors: day.active_visitors_count ?? null
      }))
  }, [dailyData])

  const configByMode: Record<ClientBaseMode, ModeConfig> = {
    clients: {
      title: clientsTitle,
      dataKey: 'clients',
      metricKey: 'total_clients_count',
      count: clientsCount,
      growth: clientsGrowth,
      retention: clientsRetention,
      valueColor: 'text-warning',
      retentionPositiveColor: 'text-success',
      stroke: '#f2b203',
      fill: '#ffd45c',
      axisColor: '#d18f00'
    },
    subscribers: {
      title: 'Подписчики',
      dataKey: 'subscribers',
      metricKey: 'active_subscribers_count',
      count: subscribersCount,
      growth: subscribersGrowth,
      retention: subscribersRetention,
      valueColor: 'text-primary',
      retentionPositiveColor: 'text-primary',
      stroke: '#2563eb',
      fill: '#93c5fd',
      axisColor: '#2563eb'
    },
    visitors: {
      title: 'Ходят регулярно',
      windowDays: 21,
      note: 'Постоянные клиенты: 3+ визита за 21 день',
      noteShort: '3+ визита за 21 день',
      tooltipValueLabel: 'чел. с 3+ визитами',
      dataKey: 'visitors',
      metricKey: 'active_visitors_count',
      count: visitorsCount,
      growth: visitorsGrowth,
      retention: undefined,
      valueColor: 'text-success',
      retentionPositiveColor: 'text-success',
      stroke: '#15803d',
      fill: '#86efac',
      axisColor: '#15803d'
    }
  }

  const modeButtons: { mode: ClientBaseMode; label: string; show: boolean }[] = [
    { mode: 'clients', label: 'Клиенты', show: true },
    { mode: 'subscribers', label: 'Подписки', show: showSubscribers },
    { mode: 'visitors', label: 'Ходят регулярно', show: true }
  ]

  // Сетка, ось X и тултип — из темы: recharts кладёт цвет в SVG-атрибуты,
  // где CSS-переменная не разрешается. Ось Y и заливка области остаются
  // цветом СЕРИИ (жёлтый «клиенты» / синий «подписки») — он несёт смысл.
  const chartTheme = useChartAxisTheme()

  const config = configByMode[currentMode]
  const hasChartData = chartData.length > 0
  // Пример считаем от ПОСЛЕДНЕЙ точки графика: абстрактное «скользящее окно»
  // читается хуже, чем две конкретные даты, которые человек видит на оси.
  const windowExample = useMemo(() => {
    if (!config.windowDays || chartData.length === 0) return null
    const last = chartData[chartData.length - 1]
    if (!last?.fullDate) return null
    return {
      pointLabel: formatChartDate(last.fullDate),
      startLabel: formatChartDate(shiftIsoDate(last.fullDate, -(config.windowDays - 1))),
    }
  }, [chartData, config.windowDays])
  const hasCountValue = config.count !== undefined && config.count !== null
  const retentionClass =
    config.retention !== undefined && config.retention < 0 ? 'text-error' : config.retentionPositiveColor
  const showRetention = !isVisitorMode(currentMode)

  return (
    <Card>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Users className={`w-5 h-5 ${config.valueColor}`} />
          <h3 className="text-sm font-medium text-muted-foreground truncate">{config.title}</h3>
        </div>

        <div className="inline-flex flex-wrap justify-end gap-0.5 rounded-lg bg-muted p-0.5 shrink-0">
          {modeButtons
            .filter((button) => button.show)
            .map((button) => (
              <button
                key={button.mode}
                type="button"
                onClick={() => setMode(button.mode)}
                className={`px-2.5 py-1 text-[11px] rounded-md transition-colors ${
                  currentMode === button.mode
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {button.label}
              </button>
            ))}
        </div>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <p className={`text-2xl font-bold ${hasCountValue ? config.valueColor : 'text-muted-foreground'}`}>
            {hasCountValue ? formatNumber(config.count) : '—'}
          </p>
          {hasCountValue && config.growth !== undefined && (
            <span className={`text-sm font-semibold ${config.growth >= 0 ? 'text-success' : 'text-error'}`}>
              {config.growth >= 0 ? '+' : ''}
              {formatNumber(config.growth)}
            </span>
          )}
        </div>

        {!showRetention && config.note && (
          <div className="flex max-w-[58%] items-start gap-1 text-xs text-muted-foreground">
            <span className="relative inline-flex items-start gap-1 group cursor-help text-right leading-tight">
              <span className="hidden sm:inline">{config.note}</span>
              <span className="sm:hidden">{config.noteShort ?? config.note}</span>
              <Info className="w-3 h-3 flex-shrink-0" />
              <span className="absolute right-0 top-full mt-1 z-10 hidden w-64 whitespace-normal rounded bg-foreground px-2 py-1.5 text-[11px] leading-snug text-background shadow group-hover:block">
                Каждая точка — итог трёх недель, а не одного дня. Значение на дату показывает, сколько человек
                приходили в студию 3 раза и чаще за 21 день до неё (включая её саму). Соседние точки
                пересекаются: клиент, побывавший в середине окна, попадает сразу в несколько дат. Наведите на
                график — там видны обе границы окна.
              </span>
            </span>
          </div>
        )}

        {showRetention && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <span className="relative inline-flex items-center gap-1 group cursor-help">
              Удержание
              <Info className="w-3 h-3 flex-shrink-0" />
              <span className="absolute right-0 top-full mt-1 z-10 hidden w-56 whitespace-normal rounded bg-foreground px-2 py-1.5 text-[11px] leading-snug text-background shadow group-hover:block">
                Доля базы, которая сохранилась к концу периода: (на&nbsp;конец − новые притоки)&nbsp;/&nbsp;на&nbsp;начало. Для подписок новые притоки включают новые подписки, переходы на подписку и возвраты. Прочерк — если нет данных о базе на начало периода.
              </span>
            </span>
            <span className={`font-semibold ${retentionClass}`}>
              {config.retention !== undefined ? formatPercentage(config.retention) : '—'}
            </span>
          </div>
        )}
      </div>

      {hasChartData && (
        <div className="mt-1.5 h-24">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`client-base-gradient-${config.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={config.fill} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={config.fill} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartTheme.grid} vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: chartTheme.axis.tick.fill }}
                tickLine={false}
                axisLine={false}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 10, fill: config.axisColor }}
                tickLine={false}
                axisLine={false}
                width={36}
                domain={['auto', 'auto']}
              />
              <Tooltip
                labelFormatter={(_, payload) => {
                  const fullDate = payload?.[0]?.payload?.fullDate
                  if (!fullDate) return ''
                  // У метрики со скользящим окном одна дата вводит в заблуждение:
                  // человек читает её как «за этот день». Печатаем обе границы.
                  if (!config.windowDays) return formatTooltipDate(fullDate)
                  const windowStart = shiftIsoDate(fullDate, -(config.windowDays - 1))
                  return `${formatTooltipDate(windowStart)} - ${formatTooltipDate(fullDate)}`
                }}
                formatter={(value: number | null) => [
                  value == null ? '—' : formatNumber(value),
                  config.tooltipValueLabel ?? config.title
                ]}
                contentStyle={{
                  ...chartTheme.tooltip,
                  borderRadius: 12,
                  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)'
                }}
              />
              <Area
                type="monotone"
                dataKey={config.dataKey}
                stroke={config.stroke}
                strokeWidth={2.5}
                fill={`url(#client-base-gradient-${config.dataKey})`}
                connectNulls
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Тултип на телефоне открывается тапом, поэтому глубину окна объясняем
          и статичной строкой — на живом примере последней точки графика. */}
      {hasChartData && config.windowDays && windowExample && (
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          Точка {windowExample.pointLabel} — это все, кто ходил 3+ раза с {windowExample.startLabel} по{' '}
          {windowExample.pointLabel}, а не посещения одного дня.
        </p>
      )}

      {!hasChartData && !hasCountValue && (
        <div className="mt-3 text-xs text-muted-foreground">
          Нет фактических данных за выбранный диапазон
        </div>
      )}
    </Card>
  )
}
